import { prefersReducedMotion } from '@/hooks/usePrefersReducedMotion';
import { isDesktopSafari, isFirefox } from '@lemonade-hq/cdk';
import { clamp, inversedLerp, lerp, RNG } from '@lemonade-hq/ts-helpers';
/* eslint-disable no-param-reassign */
/* eslint-disable class-methods-use-this */
import type { RefObject } from 'react';
import * as Render from './canvas-utils';
import { HEIGHT_MULTIPLIER, WIDTH_MULTIPLIER } from './consts';
import { BACKGROUND, MINIMAP, PLANTS } from './sprites';
import type { Coin, Colors, NonNullRef, Point, Sprite, SpriteSheetItem } from './types';
import {
  accelerate,
  easeIn,
  easeInOut,
  gradualChange,
  isIOS,
  isIOS15,
  isIpad,
  mod,
  overlap,
  percentRemaining,
  randomChoice,
  randomInt,
} from './utils';

interface InitParams {
  width: number;
  height: number;
  lanes: number;
  roadWidth: number;
  cameraHeight: number;
  drawDistance: number;
  fieldOfView: number;
  segmentLength: number;
  rumbleLength: number;
  isInGameMode: boolean;
  colors: Colors;
}

export interface Environment extends Omit<InitParams, 'colors'> {
  fps: number;
  step: number;
  centrifugal: number;
  skySpeed: number;
  hillSpeed: number;
  hillOffset: number;
  playerX: number;
  speed: number;
  maxSpeed: number;
  decel: number;
  leftKeyIsDown: boolean;
  rightKeyIsDown: boolean;
  upKeyIsDown: boolean;
  downKeyIsDown: boolean;
  isPaused: boolean;
  isMinimapVisible: boolean;
  overrideMobileAnimationStop: boolean;
  secondaryScreenView?: {
    cameraHeight?: number;
    cameraDepthMultiplier?: number;
    horizonMultiplier?: number;
    maxYOverrideMultiplier?: number;
    canvasScale?: number;
    colors?: {
      road?: string;
      background?: string;
      lane?: string;
      rumble?: string;
    };
  };
}

export type RoadGeneration = 'static' | 'random';

export interface DrivingProps {
  canvas: HTMLCanvasElement;
  colors: Colors;
  images: string[];
  onReady: (images: unknown) => void;
  renderCb?: (currSegment: Segment, env: Environment) => void;
  onCoinCollected?: (coin: Coin, env: Environment) => void;
  randomSeed?: number;
  roadGeneration?: RoadGeneration;
}

export interface Part {
  world: {
    x: number;
    y: number;
    z: number;
  };
  camera: {
    x: number;
    y: number;
    z: number;
  };
  screen: {
    scale: number;
    x: number;
    y: number;
    w: number;
  };
}

export interface Segment {
  index: number;
  p1: Part;
  p2: Part;
  curve: number;
  sprites: Sprite[];
  coins: Coin[];
  looped: boolean;
  clip: number;
}

const WINDOW_WIDTH = 1920 * WIDTH_MULTIPLIER;
const WINDOW_HEIGHT = 880 * HEIGHT_MULTIPLIER;
const WINDOW_SIZE_RATIO = WINDOW_WIDTH / WINDOW_HEIGHT;
const COIN_Y_OFFSET = -10;
const INITIAL_MAX_SPEED = 3000;
export const GAME_MAX_SPEED = 4000;

export const DEFAULT_ENVIRONMENT_VALUES: Environment = {
  fps: 60,
  step: 1 / 60,
  width: WINDOW_WIDTH,
  height: WINDOW_HEIGHT,
  centrifugal: 0,
  skySpeed: 0.0005,
  hillSpeed: 0.001,
  hillOffset: 0,
  roadWidth: 600,
  segmentLength: 200,
  rumbleLength: 3,
  lanes: 1,
  fieldOfView: 100,
  cameraHeight: 430,
  drawDistance: 350,
  playerX: 0,
  speed: 2500,
  maxSpeed: INITIAL_MAX_SPEED,
  decel: 100,
  isInGameMode: false,
  leftKeyIsDown: false,
  rightKeyIsDown: false,
  upKeyIsDown: false,
  downKeyIsDown: false,
  isPaused: false,
  isMinimapVisible: true,
  overrideMobileAnimationStop: false,
};

const ROAD = {
  LENGTH: { NONE: 0, SHORT: 25, MEDIUM: 50, LONG: 100 },
  HILL: { NONE: 0, LOW: 20, MEDIUM: 40, HIGH: 60 },
  CURVE: { NONE: 0, SLIGHT: 0.2, EASY: 2 },
};

export class Driving implements DrivingProps {
  private collectedCoins = 0;

  resolution = 0;

  trackLength = 0;

  cameraDepth = 0;

  playerZ = 0;

  position = 0;

  segments: Segment[] = [];

  env: NonNullRef<Environment>;

  canvas: HTMLCanvasElement;

  secondaryCanvas?: HTMLCanvasElement;

  images: string[];

  onReady: (images: unknown) => void;

  colors: Colors;

  renderCb?: (currSegment: Segment, env: Environment) => void;

  onCoinCollected?: (coin: Coin, env: Environment) => void;

  randomSeed?: number;

  roadGeneration: RoadGeneration;

  private rng?: RNG;

  private cachedCtx: CanvasRenderingContext2D | null = null;

  private cachedSecondaryCtx: CanvasRenderingContext2D | null = null;

  private readonly contextOptions = isIOS || isDesktopSafari() ? { alpha: false } : undefined;

  constructor(props: DrivingProps, env: RefObject<Partial<Environment>> = { current: {} }) {
    this.env = env as NonNullRef<Environment>;

    this.env.current.fps = this.env.current.fps || DEFAULT_ENVIRONMENT_VALUES.fps; // how many 'update' frames per second
    this.env.current.step = this.env.current.step || DEFAULT_ENVIRONMENT_VALUES.step; // how long is each frame (in seconds)
    this.env.current.width = this.env.current.width || DEFAULT_ENVIRONMENT_VALUES.width; // logical canvas width
    this.env.current.height = this.env.current.height || DEFAULT_ENVIRONMENT_VALUES.height; // logical canvas height
    this.env.current.centrifugal = this.env.current.centrifugal || DEFAULT_ENVIRONMENT_VALUES.centrifugal; // centrifugal force multiplier when going around curves
    this.env.current.skySpeed = this.env.current.skySpeed || DEFAULT_ENVIRONMENT_VALUES.skySpeed; // background sky layer scroll speed when going around curve (or up hill)
    this.env.current.hillSpeed = this.env.current.hillSpeed || DEFAULT_ENVIRONMENT_VALUES.hillSpeed; // background hill layer scroll speed when going around curve (or up hill)
    this.env.current.hillOffset = this.env.current.hillOffset || DEFAULT_ENVIRONMENT_VALUES.hillOffset; // current hill scroll offset
    this.env.current.roadWidth = this.env.current.roadWidth || DEFAULT_ENVIRONMENT_VALUES.roadWidth; // actually half the roads width, easier math if the road spans from -roadWidth to +roadWidth
    this.env.current.segmentLength = this.env.current.segmentLength || DEFAULT_ENVIRONMENT_VALUES.segmentLength; // length of a single segment
    this.env.current.rumbleLength = this.env.current.rumbleLength || DEFAULT_ENVIRONMENT_VALUES.rumbleLength; // number of segments per red/white rumble strip
    this.env.current.lanes = this.env.current.lanes || DEFAULT_ENVIRONMENT_VALUES.lanes; // number of lanes
    this.env.current.fieldOfView = this.env.current.fieldOfView || DEFAULT_ENVIRONMENT_VALUES.fieldOfView; // angle (degrees) for field of view
    this.env.current.cameraHeight = this.env.current.cameraHeight || DEFAULT_ENVIRONMENT_VALUES.cameraHeight; // z height of camera
    this.env.current.drawDistance = this.env.current.drawDistance || DEFAULT_ENVIRONMENT_VALUES.drawDistance; // number of segments to draw
    this.env.current.playerX = this.env.current.playerX || DEFAULT_ENVIRONMENT_VALUES.playerX; // player x offset from center of road (-1 to 1 to stay independent of roadWidth)
    this.env.current.speed = this.env.current.speed || DEFAULT_ENVIRONMENT_VALUES.speed; // current speed
    this.env.current.maxSpeed = this.env.current.maxSpeed || DEFAULT_ENVIRONMENT_VALUES.maxSpeed; // top speed (ensure we can't move more than 1 segment in a single frame to make collision detection easier)
    this.env.current.decel = this.env.current.decel || DEFAULT_ENVIRONMENT_VALUES.decel; // 'natural' deceleration rate when neither accelerating, nor braking
    this.env.current.isInGameMode = this.env.current.isInGameMode || DEFAULT_ENVIRONMENT_VALUES.isInGameMode;
    this.env.current.leftKeyIsDown = this.env.current.leftKeyIsDown || DEFAULT_ENVIRONMENT_VALUES.leftKeyIsDown;
    this.env.current.rightKeyIsDown = this.env.current.rightKeyIsDown || DEFAULT_ENVIRONMENT_VALUES.rightKeyIsDown;
    this.env.current.upKeyIsDown = this.env.current.upKeyIsDown || DEFAULT_ENVIRONMENT_VALUES.upKeyIsDown;
    this.env.current.downKeyIsDown = this.env.current.downKeyIsDown || DEFAULT_ENVIRONMENT_VALUES.downKeyIsDown;
    this.env.current.isPaused = this.env.current.isPaused || DEFAULT_ENVIRONMENT_VALUES.isPaused;
    this.env.current.isMinimapVisible =
      this.env.current.isMinimapVisible ?? DEFAULT_ENVIRONMENT_VALUES.isMinimapVisible;
    this.env.current.overrideMobileAnimationStop =
      this.env.current.overrideMobileAnimationStop ?? DEFAULT_ENVIRONMENT_VALUES.overrideMobileAnimationStop;

    this.canvas = props.canvas;
    this.images = props.images;
    this.onReady = props.onReady;
    this.colors = props.colors;
    this.renderCb = props.renderCb;
    this.onCoinCollected = props.onCoinCollected;
    this.randomSeed = props.randomSeed;
    this.roadGeneration = props.roadGeneration ?? 'static';
    if (this.randomSeed != null) {
      this.rng = new RNG(this.randomSeed);
    }
  }

  // -----
  // UTILS
  // -----
  private findSegment(index: number): Segment {
    return this.segments[mod(Math.floor(index / this.env.current.segmentLength), this.segments.length)];
  }

  private lastY(): number {
    return this.segments.length === 0 ? 0 : this.segments[this.segments.length - 1].p2.world.y;
  }

  private project(
    p: Part,
    cameraX: number,
    cameraY: number,
    cameraZ: number,
    cameraDepth: number,
    width: number,
    height: number,
    roadWidth: number,
    horizon?: number,
  ): void {
    const cam = p.camera;
    const scr = p.screen;
    const world = p.world;

    cam.x = (world.x || 0) - cameraX;
    cam.y = (world.y || 0) - cameraY;
    cam.z = (world.z || 0) - cameraZ;

    const scale = cameraDepth / cam.z;
    scr.scale = scale;

    const halfWidth = width * 0.5;
    const scaledWidth = scale * halfWidth;
    scr.x = (halfWidth + scaledWidth * cam.x + 0.5) | 0;

    const h = horizon ?? height * 0.5;
    scr.y = (h - scale * cam.y * height * 0.5 + 0.5) | 0;

    scr.w = (scale * roadWidth * halfWidth + 0.5) | 0;
  }

  async loadImages(urls: string[]): Promise<HTMLImageElement[]> {
    return new Promise(res => {
      // load multiple images and callback when ALL images have loaded
      const result: HTMLImageElement[] = [];
      let count = urls.length;

      const onload = (): void => {
        count -= 1;

        if (count === 0) res(result);
      };

      for (let n = 0; n < urls.length; n++) {
        result[n] = document.createElement('img');
        result[n].addEventListener('load', onload);
        result[n].src = urls[n];
      }
    });
  }

  // ---------------
  // BUILD GEOMETRY
  // ---------------
  private addSegment(curve: number, y: number): void {
    const n = this.segments.length;
    this.segments.push({
      index: n,
      p1: {
        world: { y: this.lastY(), z: n * this.env.current.segmentLength },
        camera: { y: 0, z: 0 },
        screen: { scale: 1 },
      },
      p2: {
        world: { y, z: (n + 1) * this.env.current.segmentLength },
        camera: { y: 0, z: 0 },
        screen: { scale: 1 },
      },
      curve,
      sprites: [],
      coins: [],
    } as unknown as Segment);
  }

  private addSprite(segmentIndex: number, sprite: SpriteSheetItem, offset: number): void {
    this.segments[segmentIndex].sprites.push({
      source: sprite,
      offset,
      scale: lerp(0.8, 1.1, Math.random()),
    });
  }

  private addRoad(enter: number, hold: number, leave: number, curve: number, y = 0): void {
    const startY = this.lastY();
    const endY = startY + y * this.env.current.segmentLength;
    const total = enter + hold + leave;

    for (let n = 0; n < enter; n++) {
      this.addSegment(easeIn(0, curve, n / enter), easeInOut(startY, endY, n / total));
    }

    for (let n = 0; n < hold; n++) {
      this.addSegment(curve, easeInOut(startY, endY, (enter + n) / total));
    }

    for (let n = 0; n < leave; n++) {
      this.addSegment(easeInOut(curve, 0, n / leave), easeInOut(startY, endY, (enter + hold + n) / total));
    }
  }

  private addHill(num = ROAD.LENGTH.MEDIUM, height = ROAD.HILL.MEDIUM): void {
    this.addRoad(num, num, num, 0, height);
  }

  private addCurve(num = ROAD.LENGTH.SHORT, curve = ROAD.CURVE.EASY, height = ROAD.HILL.NONE): void {
    this.addRoad(num, num, num, curve, height);
  }

  // -----------
  // RESET WORLD
  // -----------
  private rand(): number {
    return this.rng?.random() ?? Math.random();
  }

  private resetSprites(): void {
    // this.addSprite(this.env.current.drawDistance, BILLBOARDS[0], -3);

    // Add trees
    for (let n = 0; n < this.segments.length; n += 3) {
      this.addSprite(n, randomChoice(PLANTS, this.rng), randomChoice([1, -1], this.rng) * (2 + this.rand() * 30));
    }
  }

  private resetCoins(): void {
    for (let n = 0; n < this.segments.length; n += randomInt(15, 40, this.rng)) {
      this.segments[n].coins.push({
        type: 'point',
        collected: false,
        moveDirection: 0,
        z: n,
        offset: lerp(-1, 1, this.rand()),
        mega: n % 3 === 0 && this.rand() < 0.3,
      });
    }

    for (let n = 0; n < this.segments.length; n += randomInt(60, 100, this.rng)) {
      this.segments[n].coins.push({
        type: 'time',
        collected: false,
        moveDirection: 0,
        z: n,
        offset: lerp(-1, 1, this.rand()),
        mega: n % 3 === 0 && this.rand() < 0.3,
      });
    }
  }

  private resetStaticRoad(): void {
    this.addCurve(30, 0, 3);
    this.addCurve(ROAD.LENGTH.SHORT, -0.2, 3);
    this.addCurve(ROAD.LENGTH.SHORT, 0.2, -3);
    this.addCurve(ROAD.LENGTH.SHORT, 0.2, 3);
    this.addCurve(ROAD.LENGTH.SHORT, -0.2, -3);
    this.addCurve(ROAD.LENGTH.SHORT, -ROAD.CURVE.EASY, ROAD.HILL.NONE);
    this.addCurve(ROAD.LENGTH.SHORT, ROAD.CURVE.EASY, 10);
    this.addHill(ROAD.LENGTH.SHORT, -5);
    this.addCurve(ROAD.LENGTH.SHORT, 0.2, -3);
    this.addCurve(ROAD.LENGTH.SHORT, -0.2, 3);
    this.addCurve(ROAD.LENGTH.SHORT, 0.2, 3);
    this.addCurve(ROAD.LENGTH.SHORT, -0.2, -3);
    this.addCurve(ROAD.LENGTH.SHORT, ROAD.CURVE.EASY, -5);
    this.addCurve(ROAD.LENGTH.SHORT, -ROAD.CURVE.EASY, ROAD.HILL.NONE);
    this.addCurve(30, 0, -3);
  }

  private resetRandomRoad(): void {
    const lengths = [ROAD.LENGTH.SHORT, ROAD.LENGTH.SHORT, ROAD.LENGTH.MEDIUM];
    const curves = [
      -ROAD.CURVE.EASY,
      -ROAD.CURVE.EASY,
      -ROAD.CURVE.SLIGHT,
      -ROAD.CURVE.SLIGHT,
      -ROAD.CURVE.SLIGHT,
      ROAD.CURVE.SLIGHT,
      ROAD.CURVE.SLIGHT,
      ROAD.CURVE.SLIGHT,
      ROAD.CURVE.EASY,
      ROAD.CURVE.EASY,
    ];
    const hills = [-ROAD.HILL.LOW, ROAD.HILL.NONE, ROAD.HILL.NONE, ROAD.HILL.LOW];

    this.addCurve(30, 0, 3);

    const segmentCount = randomInt(16, 24, this.rng);
    for (let i = 0; i < segmentCount; i++) {
      const length = randomChoice(lengths, this.rng);
      const curve = randomChoice(curves, this.rng);
      const hill = randomChoice(hills, this.rng);
      this.addCurve(length, curve, hill);
    }

    this.addCurve(30, 0, -3);
  }

  private resetRoad(): void {
    this.segments = [];

    if (this.roadGeneration === 'random') {
      this.resetRandomRoad();
    } else {
      this.resetStaticRoad();
    }

    this.resetSprites();
    this.resetCoins();

    this.trackLength = this.segments.length * this.env.current.segmentLength;
  }

  reset(canvas: HTMLCanvasElement, options: Partial<InitParams> = {}): void {
    const DEVICE_PIXEL_RATIO = window.devicePixelRatio;
    const width = options.width || this.env.current.width;
    const height = width / WINDOW_SIZE_RATIO;

    canvas.width = (width * DEVICE_PIXEL_RATIO) | 0;
    canvas.height = (height * DEVICE_PIXEL_RATIO) | 0;

    this.cachedCtx = canvas.getContext('2d', this.contextOptions) as CanvasRenderingContext2D | null;
    this.cachedCtx?.scale(DEVICE_PIXEL_RATIO, DEVICE_PIXEL_RATIO);

    this.env.current.width = width;
    this.env.current.height = height;
    this.env.current.lanes = options.lanes || this.env.current.lanes;
    this.env.current.roadWidth = options.roadWidth || this.env.current.roadWidth;
    this.env.current.cameraHeight = options.cameraHeight || this.env.current.cameraHeight;
    this.env.current.drawDistance = options.drawDistance || this.env.current.drawDistance;
    this.env.current.fieldOfView = options.fieldOfView || this.env.current.fieldOfView;
    this.env.current.segmentLength = options.segmentLength || this.env.current.segmentLength;
    this.env.current.rumbleLength = options.rumbleLength || this.env.current.rumbleLength;

    this.cameraDepth = 1 / Math.tan(((this.env.current.fieldOfView / 2) * Math.PI) / 180);
    this.playerZ = this.env.current.cameraHeight * this.cameraDepth;
    this.resolution = height / 480;

    if (this.segments.length === 0 || options.segmentLength || options.rumbleLength) {
      this.resetRoad();
    }
  }

  // --------------------
  // UPDATE DRIVING WORLD
  // --------------------
  private updateSpeed(dt: number, playerSegment: Segment): void {
    if (this.env.current.isInGameMode) {
      if (this.env.current.upKeyIsDown) {
        this.env.current.speed = accelerate(this.env.current.speed, this.env.current.decel * 2, dt);
      } else if (this.env.current.downKeyIsDown) {
        this.env.current.speed = accelerate(this.env.current.speed, -this.env.current.decel * 3, dt);
      }
    } else if (
      playerSegment.curve > 0.5 ||
      playerSegment.curve < -0.5 ||
      Math.random() > 0.55 ||
      this.env.current.speed > INITIAL_MAX_SPEED
    ) {
      this.env.current.speed = accelerate(this.env.current.speed, -this.env.current.decel, dt);
    } else {
      this.env.current.speed = accelerate(this.env.current.speed, this.env.current.decel * 2, dt);
    }

    this.env.current.speed = clamp(this.env.current.speed, 0, this.env.current.maxSpeed); // or exceed maxSpeed
  }

  private updateCoins(_: number, playerSegment: Segment): void {
    const baseSegment = this.findSegment(this.position);
    const drawDistance = this.env.current.drawDistance;
    const segments = this.segments;
    const segmentsLength = segments.length;
    const baseIndex = baseSegment.index;
    const collectedCoins = this.collectedCoins;
    const shouldMoveCoin = collectedCoins > 10;
    const moveDiv = shouldMoveCoin ? clamp(collectedCoins / 3000, 0, 0.1) : 0;

    for (let n = 0; n < drawDistance; n++) {
      const segment = segments[(baseIndex + n) % segmentsLength];
      const coins = segment.coins;
      const coinsLen = coins.length;

      for (let i = 0; i < coinsLen; i++) {
        const coin = coins[i];

        if (n > 10) {
          coin.collected = false;
        }

        if (shouldMoveCoin) {
          const dir = coin.moveDirection;
          if (dir === 0) {
            coin.moveDirection = randomChoice([-1, 1]);
          } else if (dir === 1) {
            if (coin.offset < 1) coin.offset += moveDiv;
            else coin.moveDirection = -1;
          } else {
            if (coin.offset > -1) coin.offset -= moveDiv;
            else coin.moveDirection = 1;
          }
        }
      }
    }

    const playerCoins = playerSegment.coins;
    const playerCoinsLen = playerCoins.length;
    const playerX = this.env.current.playerX;

    for (let n = 0; n < playerCoinsLen; n++) {
      const coin = playerCoins[n];

      if (!coin.collected && overlap(playerX, 0.5, coin.offset, 0.2, 0.7)) {
        coin.collected = true;
        this.onCoinCollected?.(coin, this.env.current);

        if (coin.type === 'point') this.collectedCoins += 1;
        break;
      }
    }
  }

  private updatePlayerX(dt: number, playerSegment: Segment): void {
    const speedPercent = this.env.current.speed / this.env.current.maxSpeed;
    const dx = dt * 2 * speedPercent; // at top speed, should be able to cross from left to right (-1 to 1) in 1 second

    this.env.current.playerX -= dx * speedPercent * playerSegment.curve * this.env.current.centrifugal;

    if (this.env.current.isInGameMode) {
      if (this.env.current.leftKeyIsDown) this.env.current.playerX -= dx;
      else if (this.env.current.rightKeyIsDown) this.env.current.playerX += dx;
    } else if (this.env.current.playerX !== 0) {
      // Slide back to the center after a game
      if (this.env.current.playerX < 0) {
        this.env.current.playerX += dx / 2;
      } else if (this.env.current.playerX > 0) {
        this.env.current.playerX -= dx / 2;
      }

      if (this.env.current.playerX < 0.01 && this.env.current.playerX > -0.01) this.env.current.playerX = 0;
    }

    this.env.current.playerX = clamp(this.env.current.playerX, -1, 1); // don't ever let it go too far out of bounds
  }

  private update(dt: number): void {
    const playerSegment = this.findSegment(this.position + this.playerZ);
    const startPosition = this.position;

    this.position = gradualChange(this.position, dt * this.env.current.speed, this.trackLength);

    this.updateSpeed(dt, playerSegment);
    this.updatePlayerX(dt, playerSegment);

    if (this.env.current.isInGameMode) {
      this.updateCoins(dt, playerSegment);
    }

    this.env.current.hillOffset = gradualChange(
      this.env.current.hillOffset,
      (this.env.current.hillSpeed * playerSegment.curve * (this.position - startPosition)) /
        this.env.current.segmentLength,
      1,
    );
  }

  // ------
  // RENDER
  // ------
  private renderRoad(
    ctx: CanvasRenderingContext2D,
    baseSegment: Segment,
    basePercent: number,
    playerY: number,
    cameraHeight?: number,
    cameraDepth?: number,
    horizon?: number,
    maxYOverride?: number,
    colors?: Colors['road'],
  ): void {
    const env = this.env.current;
    const { width, height, roadWidth, playerX, lanes, drawDistance } = env;
    const segments = this.segments;
    const segmentsLength = segments.length;
    const baseIndex = baseSegment.index;
    const trackLength = this.trackLength;
    const position = this.position;

    let maxY = maxYOverride ?? height;
    let x = 0;
    let dx = -(baseSegment.curve * basePercent);

    const resolvedCameraHeight = cameraHeight ?? env.cameraHeight;
    const resolvedCameraDepth = cameraDepth ?? this.cameraDepth;
    const resolvedColors = colors ?? this.colors.road;
    const playerXOffset = playerX * roadWidth;
    const cameraY = playerY + resolvedCameraHeight;

    for (let n = 0; n < drawDistance; n++) {
      const segment = segments[mod(baseIndex + n, segmentsLength)];
      const looped = segment.index < baseIndex;
      segment.looped = looped;
      segment.clip = maxY;

      const positionOffset = looped ? trackLength : 0;

      this.project(
        segment.p1,
        playerXOffset - x,
        cameraY,
        position - positionOffset,
        resolvedCameraDepth,
        width,
        height,
        roadWidth,
        horizon,
      );
      this.project(
        segment.p2,
        playerXOffset - x - dx,
        cameraY,
        position - positionOffset,
        resolvedCameraDepth,
        width,
        height,
        roadWidth,
        horizon,
      );

      x += dx;
      dx += segment.curve;

      const p1Screen = segment.p1.screen;
      const p2Screen = segment.p2.screen;

      if (segment.p1.camera.z <= resolvedCameraDepth || p2Screen.y >= p1Screen.y || p2Screen.y >= maxY) {
        continue;
      }

      Render.road(
        ctx,
        width,
        p1Screen.x,
        p1Screen.y,
        p1Screen.w,
        p2Screen.x,
        p2Screen.y,
        p2Screen.w,
        resolvedColors,
        lanes,
        ((segment.index / 3) | 0) % 2 === 0,
      );

      maxY = p1Screen.y;
    }
  }

  private renderSprites(
    ctx: CanvasRenderingContext2D,
    baseSegment: Segment,
    sprites: HTMLImageElement,
    colors?: Colors['coins'],
  ): void {
    const env = this.env.current;
    const { drawDistance, isInGameMode, roadWidth, width } = env;
    const segments = this.segments;
    const segmentsLength = segments.length;
    const baseIndex = baseSegment.index;
    const roadWidthTimesWidth = roadWidth * width * 0.5;
    const coinColors = colors ?? this.colors.coins;

    for (let n = drawDistance - 1; n > 0; n--) {
      const segment = segments[(baseIndex + n) % segmentsLength];
      const p1Screen = segment.p1.screen;
      const screenX = p1Screen.x;
      const screenY = p1Screen.y;
      const screenScale = p1Screen.scale;
      const clip = segment.clip;

      if (isInGameMode) {
        const coins = segment.coins;
        const coinsLen = coins.length;
        for (let i = 0; i < coinsLen; i++) {
          const coin = coins[i];
          const coinX = screenX + screenScale * coin.offset * roadWidthTimesWidth;
          const coinY = screenY + screenScale * (coin.mega ? -200000 : -270000) * 0.5 + COIN_Y_OFFSET;

          if (clip > coinY) {
            const coinColor = coinColors[coin.type];
            const megaMultiplier = coin.mega ? 2 : 1;
            Render.circle(
              ctx,
              coinX,
              coinY,
              screenScale * 40000 * megaMultiplier,
              coinColor.color,
              screenScale * 70000 * megaMultiplier * 2,
              coinColor.border,
              screenScale * 11000,
            );
          }
        }
      }

      const spritesList = segment.sprites;
      const spritesLen = spritesList.length;
      for (let i = 0; i < spritesLen; i++) {
        const sprite = spritesList[i];
        const spriteScale = screenScale * sprite.scale;
        const spriteX = screenX + spriteScale * sprite.offset * roadWidthTimesWidth;

        Render.sprite(
          ctx,
          width,
          roadWidth,
          sprites,
          sprite.source,
          spriteScale,
          spriteX,
          screenY,
          sprite.offset < 0 ? -1 : 0,
          -1,
          clip,
        );
      }
    }
  }

  private renderMinimap(
    ctx: CanvasRenderingContext2D,
    baseSegment: Segment,
    basePercent: number,
    playerY: number,
  ): void {
    if (!this.env.current.isMinimapVisible) return;

    const env = this.env.current;
    const { width, height, roadWidth, cameraHeight, drawDistance } = env;
    const segments = this.segments;
    const segmentsLength = segments.length;
    const baseIndex = baseSegment.index;
    const position = this.position;
    const cameraDepth = this.cameraDepth;

    const minimap = MINIMAP;
    const minimapW = minimap.w;
    const minimapX = minimap.x;
    const minimapY = minimap.y;
    const minimapH = minimap.h;

    const dy = ((baseSegment.p1.world.y ?? 0) - (baseSegment.p2.world.y ?? 0)) * 0.2;
    Render.roundRect(ctx, minimapX, minimapY + dy, minimapW, minimapH, 45, this.colors.minimap.background);

    const minimapCenterPoint = 0.65;
    const minimapDistance = (drawDistance * minimapCenterPoint + 0.5) | 0;
    const minimapScale = minimapW / MINIMAP.w;
    const minimapXScale = 500 * minimapScale;
    const cameraY = playerY + cameraHeight;

    let x = 0;
    let dx = -baseSegment.curve * basePercent;
    for (let n = 0; n < minimapDistance; n++) {
      const segment = segments[mod(baseIndex + n, segmentsLength)];
      this.project(segment.p1, -x, cameraY, position + n, cameraDepth, width, height, roadWidth);
      x += dx;
      dx += segment.curve;
    }

    x = 0;
    dx = baseSegment.curve * basePercent;
    for (let n = 0; n < minimapDistance; n++) {
      const segment = segments[mod(baseIndex - n, segmentsLength)];
      this.project(segment.p1, -x, cameraY, position - n, cameraDepth, width, height, roadWidth);
      x += dx;
      dx += segment.curve;
    }

    const drawDistanceMinusMinimapDistance = drawDistance - minimapDistance;
    const pointCount = ((drawDistanceMinusMinimapDistance + minimapDistance) / 3) | 0;
    const points: Point[] = Array.from({ length: pointCount });
    const xScaleHalf = minimapXScale * 0.5;
    const xMin = minimapX - xScaleHalf;
    const xMax = minimapX + minimapW + xScaleHalf;
    const yBottom = minimapY + dy + minimapH;
    const yTop = minimapY + dy;

    let pointIdx = 0;
    for (let n = -minimapDistance; n < drawDistanceMinusMinimapDistance; n += 3) {
      const segment = segments[mod(baseIndex + n, segmentsLength)];
      const px = lerp(xMin, xMax, inversedLerp(0, width, segment.p1.screen.x));
      const py = lerp(yBottom, yTop, inversedLerp(-minimapDistance, drawDistanceMinusMinimapDistance, n));
      points[pointIdx++] = { x: px, y: py };
    }

    const minimapColors = this.colors.minimap;
    Render.line(ctx, points, 10 * minimapScale, minimapColors.road);
    Render.line(ctx, points, 2 * minimapScale, minimapColors.line, minimapCenterPoint);

    Render.circle(
      ctx,
      lerp(minimapX, minimapX + minimapW, 0.5),
      lerp(yBottom, yTop, minimapCenterPoint),
      5 * minimapScale,
      minimapColors.marker,
      20 * minimapScale,
      minimapColors.markerBorder,
      1,
    );
  }

  private render(ctx: CanvasRenderingContext2D, background: HTMLImageElement, sprites: HTMLImageElement): void {
    const env = this.env.current;
    const { width, height, segmentLength, hillOffset, hillSpeed, secondaryScreenView } = env;

    const baseSegment = this.findSegment(this.position);
    const basePercent = percentRemaining(this.position, segmentLength);
    const playerSegment = this.findSegment(this.position + this.playerZ);
    const playerPercent = percentRemaining(this.position + this.playerZ, segmentLength);
    const playerY = lerp(playerSegment.p1.world.y, playerSegment.p2.world.y, playerPercent);

    ctx.clearRect(0, 0, width, height);

    Render.background(
      ctx,
      background,
      width,
      height,
      BACKGROUND.HILLS,
      hillOffset,
      this.resolution * hillSpeed * playerY,
    );

    this.renderRoad(ctx, baseSegment, basePercent, playerY);
    this.renderSprites(ctx, baseSegment, sprites);
    this.renderMinimap(ctx, baseSegment, basePercent, playerY);

    const sCtx = this.cachedSecondaryCtx;
    if (this.secondaryCanvas && sCtx) {
      sCtx.clearRect(0, 0, width, height);

      const config = secondaryScreenView || {};
      const cameraHeight = config.cameraHeight ?? 4000;
      const cameraDepth = this.cameraDepth * (config.cameraDepthMultiplier ?? 0.4);
      const horizon = height * (config.horizonMultiplier ?? 0.15);
      const maxYOverride = height * (config.maxYOverrideMultiplier ?? 10);

      const roadColors = config.colors
        ? {
            ...this.colors.road,
            background: config.colors.road ?? this.colors.road.background,
            grass: config.colors.background ?? this.colors.road.grass,
            lane: config.colors.lane ?? this.colors.road.lane,
            rumble: config.colors.rumble ?? this.colors.road.rumble,
            rumbleShadow: config.colors.rumble ?? this.colors.road.rumbleShadow,
          }
        : undefined;

      this.renderRoad(
        sCtx,
        baseSegment,
        basePercent,
        playerY,
        cameraHeight,
        cameraDepth,
        horizon,
        maxYOverride,
        roadColors,
      );
      this.renderSprites(sCtx, baseSegment, sprites);
    }

    this.renderCb?.(playerSegment, env);
  }

  // -----------
  // VROOM VROOM
  // -----------
  async run(): Promise<void> {
    const images = await this.loadImages(this.images);

    const [background, sprites] = images;
    this.onReady(images);
    this.reset(this.canvas);

    let last = performance.now();
    let gdt = 0;

    const stopAnimationCheck = isIOS && !isIpad();
    const isFirefoxBrowser = isFirefox();
    const isIOS15Browser = isIOS15();

    const frame = (): void => {
      const now = performance.now();
      const dt = Math.min(1, (now - last) * 0.001);
      gdt += dt;

      const { step, isPaused } = this.env.current;

      while (gdt > step) {
        gdt -= step;
        if (!isPaused) this.update(step);
      }

      const ctx = this.cachedCtx;
      if (!ctx) return;

      if (!isPaused) this.render(ctx, background, sprites);
      last = now;

      const stopAnimation = stopAnimationCheck && !this.env.current.overrideMobileAnimationStop;
      if (!stopAnimation && !isFirefoxBrowser && !isIOS15Browser && !prefersReducedMotion()) {
        requestAnimationFrame(frame);
      }
    };

    frame();
  }

  pause(): void {
    this.env.current.isPaused = true;
  }

  resume(): void {
    this.env.current.isPaused = false;
  }

  setSecondaryCanvas(canvas: HTMLCanvasElement): void {
    this.secondaryCanvas = canvas;
    const width = this.env.current.width;
    const height = width / WINDOW_SIZE_RATIO;
    const DEVICE_PIXEL_RATIO = window.devicePixelRatio;
    canvas.width = (width * DEVICE_PIXEL_RATIO) | 0;
    canvas.height = (height * DEVICE_PIXEL_RATIO) | 0;
    this.cachedSecondaryCtx = canvas.getContext('2d', this.contextOptions) as CanvasRenderingContext2D | null;
    this.cachedSecondaryCtx?.scale(DEVICE_PIXEL_RATIO, DEVICE_PIXEL_RATIO);
  }

  toggleMinimap(force?: boolean): void {
    this.env.current.isMinimapVisible = force != null ? force : !this.env.current.isMinimapVisible;
  }

  startGame(): void {
    this.env.current.isInGameMode = true;
    this.env.current.lanes = 3;
    this.env.current.centrifugal = 0.3;
    this.env.current.maxSpeed = 4000;
  }

  endGame(): number {
    this.env.current.isInGameMode = false;
    this.env.current.lanes = DEFAULT_ENVIRONMENT_VALUES.lanes;
    this.env.current.centrifugal = DEFAULT_ENVIRONMENT_VALUES.centrifugal;
    this.env.current.maxSpeed = DEFAULT_ENVIRONMENT_VALUES.maxSpeed;
    this.env.current.leftKeyIsDown = false;
    this.env.current.rightKeyIsDown = false;
    this.env.current.upKeyIsDown = false;
    this.env.current.downKeyIsDown = false;

    const coins = this.collectedCoins;
    this.collectedCoins = 0;

    return coins;
  }
}
